import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // 30s ceiling so any single API call (especially /auth/me during cold
  // starts) can't hang the entire app forever. Without this, a flaky
  // network or a hung Cloudflare edge will leave users stuck on the
  // "[recon] verifying clearance" loading screen indefinitely.
  timeout: 30_000,
});

export async function exchangeSession(session_id) {
  const { data } = await api.post("/auth/session", { session_id });
  return data;
}

export async function getMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function getBackendVersion() {
  const { data } = await api.get("/version");
  return data;
}

export async function logout() {
  await api.post("/auth/logout");
}

export async function runScan() {
  const { data } = await api.post("/niches/scan");
  return data; // { scan_id, status: "running" }
}

export async function getScan(scan_id) {
  const { data } = await api.get(`/scans/${scan_id}`);
  return data;
}

export async function listNiches(bookmarked) {
  const params = bookmarked === undefined ? {} : { bookmarked };
  const { data } = await api.get("/niches", { params });
  return data;
}

export async function getNiche(id) {
  const { data } = await api.get(`/niches/${id}`);
  return data;
}

export async function toggleBookmark(id) {
  const { data } = await api.post(`/niches/${id}/bookmark`);
  return data;
}

export async function listScans() {
  const { data } = await api.get("/scans");
  return data;
}

export async function clearNiches(keepBookmarked = true) {
  const { data } = await api.delete("/niches", {
    params: { keep_bookmarked: keepBookmarked },
  });
  return data;
}

export async function createChannelKit(nicheId) {
  const { data } = await api.post(`/niches/${nicheId}/channel-kit`);
  return data;
}

export async function getLatestChannelKit(nicheId) {
  const { data } = await api.get(`/niches/${nicheId}/channel-kit`);
  return data;
}

export async function getChannelKit(kitId) {
  const { data } = await api.get(`/channel-kits/${kitId}`);
  return data;
}

export async function searchChannels(query, limit = 50, searchType = "niche") {
  const { data } = await api.get("/channels/search", {
    params: { q: query, limit, search_type: searchType },
  });
  return data;
}

// --- Create Channel wizard ---
export async function suggestChannelNames(topic, exclude = [], count = 5) {
  const { data } = await api.post("/create-channel/names", {
    topic, exclude, count,
  });
  return data; // { topic, names: [{name, handle, why}] }
}

export async function checkChannelName(name) {
  const { data } = await api.get("/create-channel/check-name", {
    params: { name },
  });
  return data; // { name, exact_match, matches: [...] }
}

export async function generateChannelGraphics({ name, handle, topic, style_hint, source_niche, source_channel_id, character_ids, style_preset, banner_provider }) {
  const { data } = await api.post("/create-channel/graphics", {
    name, handle, topic, style_hint, source_niche, source_channel_id, character_ids, style_preset, banner_provider,
  });
  return data; // { job_id, status }
}

export async function regenerateChannelGraphics(jobId, characterIds = null, stylePreset = null, bannerProvider = null) {
  const body = {};
  if (characterIds !== null) body.character_ids = characterIds;
  if (stylePreset !== null) body.style_preset = stylePreset;
  if (bannerProvider !== null) body.banner_provider = bannerProvider;
  const { data } = await api.post(
    `/create-channel/graphics/${jobId}/regenerate`,
    body,
  );
  return data;
}

export async function listStylePresets() {
  const { data } = await api.get("/style-presets");
  return data.presets;
}

export async function generateStylePreview(presetKey, { channelHandle = "", force = false } = {}) {
  // Image generation takes 30-90s — override the 30s default timeout.
  const { data } = await api.post(
    `/style-presets/${presetKey}/preview`,
    { channel_handle: channelHandle, force },
    { timeout: 180_000 },
  );
  return data;
}

export async function analyzeReferenceVideo({ url, channelHandle }) {
  // Backend now returns immediately with a `status: 'running'` stub
  // and does the heavy pipeline in a background task. The caller
  // polls `getReferenceVideo` until `status` becomes 'complete' or
  // 'failed' — this dodges Cloudflare's ~100s HTTP hop timeout that
  // was killing sync requests on production.
  const { data } = await api.post(
    "/reference-videos/analyze",
    { url, channel_handle: channelHandle },
  );
  return data.reference;
}

// ---------- Publish a stitched storyboard to YouTube ----------
// Uploads the assembled MP4 (from GridFS on the server) to the
// user's linked YouTube channel via the Data API. Defaults to
// `privacy_status="private"` so a mis-click never goes live —
// user reviews in YT Studio and flips public when ready.

export async function publishStoryboardToYouTube(
  storyboardId,
  { title, description, privacyStatus = "private" } = {},
) {
  const body = { privacy_status: privacyStatus };
  if (title !== undefined) body.title = title;
  if (description !== undefined) body.description = description;
  const { data } = await api.post(
    `/storyboards/${encodeURIComponent(storyboardId)}/publish-to-youtube`,
    body,
    // YouTube upload can take a while for big files. Give it a
    // generous 10 min ceiling on the client too.
    { timeout: 10 * 60 * 1000 },
  );
  return data;
}


export async function getReferenceVideo(referenceId) {
  const { data } = await api.get(
    `/reference-videos/${encodeURIComponent(referenceId)}`,
  );
  return data.reference;
}

export async function listReferenceVideos(channelHandle) {
  const { data } = await api.get("/reference-videos", {
    params: { channel_handle: channelHandle },
  });
  return data.items || [];
}

export async function deleteReferenceVideo(referenceId) {
  const { data } = await api.delete(
    `/reference-videos/${encodeURIComponent(referenceId)}`,
  );
  return data;
}

export async function spawnStoryboardFromReferenceSeed(referenceId, seedIdx) {
  const { data } = await api.post(
    `/reference-videos/${encodeURIComponent(referenceId)}/spawn/${seedIdx}`,
    {},
    { timeout: 90 * 1000 },
  );
  return data;
}

// Generate N more idea seeds for an already-analyzed reference. The
// backend appends them to `idea_seeds` on the reference doc so a
// refetch of the reference picks them up. Backend also de-dupes
// against existing titles, so this is safe to click repeatedly.
export async function generateMoreReferenceSeeds(referenceId, count = 10) {
  const { data } = await api.post(
    `/reference-videos/${encodeURIComponent(referenceId)}/generate-more-seeds`,
    { count },
    // GPT-5.2 with a full transcript in context routinely takes 30-60s.
    // Give the client plenty of headroom before it gives up.
    { timeout: 120 * 1000 },
  );
  return data;
}

// Dismiss a single reference-video seed the user isn't interested
// in. Backend marks it `consumed: true, dismissed: true` — the UI
// filters it out same as an already-spawned seed.
export async function dismissReferenceSeed(referenceId, seedIdx) {
  const { data } = await api.post(
    `/reference-videos/${encodeURIComponent(referenceId)}/seeds/${seedIdx}/dismiss`,
  );
  return data;
}

// ---------- Per-channel settings ----------
// The defaults every new storyboard on a channel inherits:
//   • style_preset      (pixar_3d, anime_2d, stick_figure, ...)
//   • post_format       ("video" = per-scene MP4 previews,
//                        "stills" = Ken-Burns slideshow, no previews)
//   • default_voice_id  (ElevenLabs voice for narration)
//   • character_ids     (default cast auto-attached to new boards)
// Both endpoints are auth-required.

export async function getChannelSettings(handle) {
  const { data } = await api.get(
    `/channel-settings/${encodeURIComponent(handle)}`,
  );
  return data.settings;
}

export async function putChannelSettings(handle, body) {
  const { data } = await api.put(
    `/channel-settings/${encodeURIComponent(handle)}`,
    body,
  );
  return data.settings;
}

export async function getChannelGraphics(jobId) {
  const { data } = await api.get(`/create-channel/graphics/${jobId}`);
  return data;
}

export async function listChannelCreations() {
  const { data } = await api.get("/create-channel/list");
  return data; // { items: [...], count }
}

export async function editChannelCreation(jobId, { name, handle } = {}) {
  const body = {};
  if (name !== undefined) body.name = name;
  if (handle !== undefined) body.handle = handle;
  const { data } = await api.patch(`/create-channel/list/${jobId}`, body);
  return data;
}

// ---------- YouTube channel branding publisher ----------

export async function getYouTubeLinkStatus() {
  const { data } = await api.get("/youtube-oauth/status");
  return data; // { linked, configured, channel_id?, channel_title? }
}

export async function startYouTubeLink({ next = "" } = {}) {
  // Returns the Google consent URL — caller opens it in a new tab.
  // Optional `next` is a same-origin relative path the OAuth callback
  // will redirect back to after linking (e.g. `/active-channels/UC…`).
  const params = next ? { next } : undefined;
  const { data } = await api.get("/youtube-oauth/start", { params });
  return data.authorize_url;
}

export async function unlinkYouTube() {
  const { data } = await api.post("/youtube-oauth/unlink");
  return data;
}

export async function applyKitToYouTube(jobId, { banner_choice = "banner_1", overwrite_title = false } = {}) {
  const { data } = await api.post(
    `/create-channel/${jobId}/apply-to-youtube`,
    { banner_choice, overwrite_title },
  );
  return data;
}

export async function deleteChannelCreation(jobId) {
  const { data } = await api.delete(`/create-channel/list/${jobId}`);
  return data; // { job_id, deleted }
}

export async function cancelChannelGraphics(jobId) {
  const { data } = await api.post(`/create-channel/graphics/${jobId}/cancel`);
  return data;
}

export function channelGraphicsDownloadUrl(jobId) {
  return `${API}/create-channel/graphics/${jobId}/download`;
}

// --- Channel intro / outro bumpers ---
// Per-channel MP4 clips that get sandwiched around the final stitched
// video. Keyed on the channel-kit's job_id server-side; blob_id in the
// returned doc is used to build a playback URL.
export async function listChannelBumpers(jobId) {
  const { data } = await api.get(`/create-channel/kit/${jobId}/bumpers`);
  return data; // { job_id, bumpers: { intro: doc|null, outro: doc|null } }
}

export async function uploadChannelBumper(jobId, kind, file) {
  const fd = new FormData();
  fd.append("kind", kind);
  fd.append("file", file);
  const { data } = await api.post(
    `/create-channel/kit/${jobId}/bumpers`, fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
  );
  return data; // { job_id, kind, bumper: doc }
}

export async function deleteChannelBumper(jobId, kind) {
  const { data } = await api.delete(`/create-channel/kit/${jobId}/bumpers/${kind}`);
  return data;
}

// --- Channel-level aspiration thumbnail references ---
export async function listChannelThumbnailRefs(jobId) {
  const { data } = await api.get(`/create-channel/kit/${jobId}/thumbnail-refs`);
  return data;
}

export async function uploadChannelThumbnailRef(jobId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(
    `/create-channel/kit/${jobId}/thumbnail-refs`, fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
  );
  return data;
}

export async function deleteChannelThumbnailRef(jobId, refId) {
  const { data } = await api.delete(`/create-channel/kit/${jobId}/thumbnail-refs/${refId}`);
  return data;
}

export async function backfillChannelThumbnailRefProfiles(jobId, { force = false } = {}) {
  // Vision call is 5-10s per ref → allow up to ~2 min for a full
  // backfill of the 4-ref cap. `force=true` re-extracts every ref
  // even if it already has a profile (used after we ship a stronger
  // extractor prompt so users don't have to re-upload).
  const params = force ? "?force=1" : "";
  const { data } = await api.post(
    `/create-channel/kit/${jobId}/thumbnail-refs/backfill${params}`,
    {},
    { timeout: 180_000 },
  );
  return data;
}

export function channelThumbnailRefUrl(previewUrl) {
  if (!previewUrl) return "";
  if (previewUrl.startsWith("http")) return previewUrl;
  return `${BACKEND_URL}${previewUrl}`;
}

export function channelBumperPreviewUrl(previewUrl) {
  if (!previewUrl) return "";
  if (previewUrl.startsWith("http")) return previewUrl;
  return `${BACKEND_URL}${previewUrl}`;
}

// --- Storyboard-scoped bumpers (per-video override) ---
export async function listStoryboardBumpers(storyboardId) {
  const { data } = await api.get(`/storyboards/${storyboardId}/bumpers`);
  return data; // { storyboard_id, bumpers: {intro,outro}, channel_defaults: {...} }
}

export async function uploadStoryboardBumper(storyboardId, kind, file) {
  const fd = new FormData();
  fd.append("kind", kind);
  fd.append("file", file);
  const { data } = await api.post(
    `/storyboards/${storyboardId}/bumpers`, fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
  );
  return data;
}

export async function deleteStoryboardBumper(storyboardId, kind) {
  const { data } = await api.delete(`/storyboards/${storyboardId}/bumpers/${kind}`);
  return data;
}

// --- Top Earners ---
export async function getTopEarners() {
  const { data } = await api.get("/top-earners");
  return data;
}

export async function triggerTopEarnersScan(force = false) {
  const { data } = await api.post(`/top-earners/scan${force ? "?force=true" : ""}`);
  return data;
}

export async function getTopEarnersScan(scanId) {
  const { data } = await api.get(`/top-earners/scans/${scanId}`);
  return data;
}

// --- Niche analysis ---
export async function analyzeNiche(nicheId, force = false) {
  const { data } = await api.post(
    `/niches/${nicheId}/analyze${force ? "?force=true" : ""}`
  );
  return data; // { niche_id, analysis, from_cache }
}

// --- Best Niche Opportunities ---
export async function getBestNiches() {
  const { data } = await api.get("/best-niches");
  return data;
}

export async function triggerBestNiches(force = false) {
  const { data } = await api.post(`/best-niches/scan${force ? "?force=true" : ""}`);
  return data;
}

export async function getBestNichesScan(scanId) {
  const { data } = await api.get(`/best-niches/scans/${scanId}`);
  return data;
}

// --- Top Revenue Channels (six-figure monthly) ---
export async function getTopRevenue() {
  const { data } = await api.get("/top-revenue");
  return data;
}
export async function triggerTopRevenueScan(force = false) {
  const { data } = await api.post(`/top-revenue/scan${force ? "?force=true" : ""}`);
  return data;
}
export async function getTopRevenueScan(scanId) {
  const { data } = await api.get(`/top-revenue/scans/${scanId}`);
  return data;
}
// --- Mid-Revenue ($50k-$100k monthly band) — same shape as Top Revenue ---
export async function getMidRevenue() {
  const { data } = await api.get("/mid-revenue");
  return data;
}
export async function triggerMidRevenueScan(force = false) {
  const { data } = await api.post(`/mid-revenue/scan${force ? "?force=true" : ""}`);
  return data;
}
export async function getMidRevenueScan(scanId) {
  const { data } = await api.get(`/mid-revenue/scans/${scanId}`);
  return data;
}
// --- Low-Revenue ($10k-$50k monthly band) — same shape as Mid ---
export async function getLowRevenue() {
  const { data } = await api.get("/low-revenue");
  return data;
}
export async function triggerLowRevenueScan(force = false) {
  const { data } = await api.post(`/low-revenue/scan${force ? "?force=true" : ""}`);
  return data;
}
export async function getLowRevenueScan(scanId) {
  const { data } = await api.get(`/low-revenue/scans/${scanId}`);
  return data;
}

// --- Channel Niche Playbook (AI analysis of a reference channel) ---
export async function triggerChannelReverse(channelId, force = false) {
  const { data } = await api.post(
    `/channels/${channelId}/reverse-engineer${force ? "?force=true" : ""}`
  );
  return data;
}
export async function getChannelReverseJob(jobId) {
  const { data } = await api.get(`/channels/reverse-engineer/${jobId}`);
  return data;
}

// --- Auto-video (Phase 3) ---
export async function triggerAutoVideo(channel_id, idea_index = 0, voice_id = null) {
  const payload = { channel_id, idea_index };
  if (voice_id) payload.voice_id = voice_id;
  const { data } = await api.post("/auto-video", payload);
  return data;
}
export async function getAutoVideo(jobId) {
  // Cache-buster `_t` query param defeats any CDN / browser HTTP cache
  // that might serve a stale "running" doc after the render completes —
  // the polling loop must see fresh state to transition the UI to the
  // complete view.
  const { data } = await api.get(`/auto-video/${jobId}`, {
    params: { _t: Date.now() },
  });
  return data;
}
export async function listAutoVideos() {
  const { data } = await api.get("/auto-video");
  return data;
}

export async function deleteAutoVideo(jobId) {
  const { data } = await api.delete(`/auto-video/${jobId}`);
  return data;
}
export async function insertSubjectIntoVideo(jobId, file, position, mode = "full") {
  const form = new FormData();
  form.append("file", file);
  form.append("position", position);
  form.append("mode", mode);
  const { data } = await api.post(
    `/auto-video/${jobId}/insert-subject`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function approveSubjectPreview(jobId) {
  const { data } = await api.post(`/auto-video/${jobId}/insert-subject/approve`);
  return data;
}

export async function listSuggestions() {
  const { data } = await api.get("/auto-video-suggestions");
  return data;
}
export async function generateSuggestions(channelId, n = 10) {
  const { data } = await api.post("/auto-video-suggestions/generate", {
    channel_id: channelId,
    n,
  });
  return data;
}
export async function dismissSuggestion(suggestionId) {
  const { data } = await api.delete(`/auto-video-suggestions/${suggestionId}`);
  return data;
}
export async function autoCreateFromSuggestion(suggestionId) {
  const { data } = await api.post(
    `/auto-video-suggestions/${suggestionId}/auto-create`,
  );
  return data;
}

export async function restoreRenderVersion(jobId, version) {
  const { data } = await api.post(`/auto-video/${jobId}/restore-version`, {
    version,
  });
  return data;
}

export async function listVoices() {
  const { data } = await api.get("/auto-video-voices");
  return data;
}

// ---------- Phase 4 · Active Channels ----------
export async function addActiveChannel({ url_or_handle, kit_id, niche_label }) {
  const { data } = await api.post("/active-channels", {
    url_or_handle,
    kit_id,
    niche_label,
  });
  return data.channel;
}
export async function listActiveChannels() {
  const { data } = await api.get("/active-channels");
  return data.channels || [];
}
export async function getActiveChannelDetail(channelId) {
  const { data } = await api.get(`/active-channels/${channelId}`);
  return data;
}
export async function refreshActiveChannel(channelId) {
  const { data } = await api.post(`/active-channels/${channelId}/refresh`);
  return data.channel;
}
export async function deleteActiveChannel(channelId) {
  const { data } = await api.delete(`/active-channels/${channelId}`);
  return data;
}
export async function createCustomStoryboard({ title, story, channel_id, channel_handle, character_ids, target_scenes }) {
  // GPT-5.2 breaks a long story into up to 24 scenes (each with 6
  // fields) which routinely takes 45-90s to complete. The default
  // axios timeout of 30s was cutting the connection before the
  // backend finished — producing a bogus "Couldn't build the
  // story" toast even when the backend succeeded a minute later.
  // 3 minutes matches the ceiling of other GPT-heavy endpoints in
  // this file and stays inside Cloudflare's ~100s HTTP hop only
  // because this call streams no keep-alive body — the whole thing
  // arrives as one JSON blob at the end.
  const { data } = await api.post(
    "/storyboards/custom-story",
    {
      title,
      story,
      channel_id,
      channel_handle,
      character_ids: character_ids || [],
      target_scenes,
    },
    { timeout: 180_000 },
  );
  return data;
}
export async function analyzeActiveChannel(channelId, { force = false } = {}) {
  const { data } = await api.post(
    `/active-channels/${channelId}/analyze`,
    { force },
  );
  return data;
}
export async function generateNextVideoForActiveChannel(channelId) {
  const { data } = await api.post(
    `/active-channels/${channelId}/generate-next-video`,
  );
  return data;
}
export async function loadIdeaBank(channelId, { force = false, count = 20 } = {}) {
  const { data } = await api.post(
    `/active-channels/${channelId}/idea-bank`,
    { force, count },
  );
  return data;
}
// Save (or clear, with empty string) the creator's voice override for the
// channel's Idea Bank. The backend invalidates the cached 20-idea batch on
// save so the next loadIdeaBank() call re-rolls under the new directive.
export async function setChannelVoice(channelId, voice) {
  const { data } = await api.put(
    `/active-channels/${channelId}/channel-voice`,
    { voice: voice || "" },
  );
  return data;
}
// Force-rebuild the character bible + thumbnail style brief from the
// channel's top 3 real thumbnails. Used by the "Re-audit style" button
// when the auto-extracted bible/brief came out empty or weak. Returns
// the freshly-extracted JSON so the UI can show what was captured.
export async function refreshChannelStyleAudit(channelId) {
  const { data } = await api.post(
    `/active-channels/${channelId}/style-audit/refresh`,
  );
  return data;
}
export async function getChannelStyleAudit(channelId) {
  const { data } = await api.get(
    `/active-channels/${channelId}/style-audit`,
    { params: { _t: Date.now() } },
  );
  return data;
}
// Manually override the character bible — useful when Claude Vision
// got Bella's breed/fur description wrong. Posted bible is pinned
// (manual_override=True) and survives auto-refresh cycles.
export async function updateCharacterBible(channelId, payload) {
  const { data } = await api.put(
    `/active-channels/${channelId}/character-bible`,
    payload || {},
  );
  return data;
}
export async function materializeIdea(channelId, ideaId) {
  const { data } = await api.post(
    `/active-channels/${channelId}/idea-bank/materialize`,
    { idea_id: ideaId },
  );
  return data;
}
// Poll the background-thumbnail status after materializeIdea returns.
// Status flips from "generating" → "ready" (thumbnail_url populated) or
// "failed" (thumbnail_error populated) once the bg task finishes.
export async function getMaterializeThumbnailStatus(channelId, ideaId) {
  const { data } = await api.get(
    `/active-channels/${channelId}/idea-bank/${ideaId}/thumbnail-status`,
    { params: { _t: Date.now() } },
  );
  return data;
}
export async function launchIdeaVideo(channelId, ideaId) {
  const { data } = await api.post(
    `/active-channels/${channelId}/idea-bank/${ideaId}/launch`,
  );
  return data;
}

// ---------- Scene Approval (Active-Channels two-step pipeline) ----------
export async function getSceneApprovalState(jobId) {
  const { data } = await api.get(
    `/auto-video/${jobId}/scenes/approval-state`,
    { params: { _t: Date.now() } },  // bust cache during poll
  );
  return data;
}
export async function approveScene(jobId, sceneIdx) {
  const { data } = await api.post(
    `/auto-video/${jobId}/scenes/${sceneIdx}/approve`,
  );
  return data;
}
export async function unapproveScene(jobId, sceneIdx) {
  const { data } = await api.post(
    `/auto-video/${jobId}/scenes/${sceneIdx}/unapprove`,
  );
  return data;
}
export async function regenerateScene(jobId, sceneIdx, prompt = "") {
  const { data } = await api.post(
    `/auto-video/${jobId}/scenes/${sceneIdx}/regenerate`,
    { prompt: prompt || "" },
  );
  return data;
}
export async function generateAllScenes(jobId) {
  const { data } = await api.post(
    `/auto-video/${jobId}/scenes/generate-all`,
  );
  return data;
}

// Sends ONE scene image to Veo3 → 8-second animated MP4. Background job;
// poll /scenes/approval-state for scene_animation_status[idx] transitions.
export async function animateScene(jobId, sceneIdx) {
  const { data } = await api.post(
    `/auto-video/${jobId}/scenes/${sceneIdx}/animate`,
  );
  return data;
}

// Persist the user's edited animate-prompt for ONE scene. Empty/blank
// `prompt` clears the override (next animate falls back to the
// LLM-generated scene description).
export async function setSceneAnimationPrompt(jobId, sceneIdx, prompt) {
  const { data } = await api.patch(
    `/auto-video/${jobId}/scenes/${sceneIdx}/animation-prompt`,
    { prompt: prompt || "" },
  );
  return data;
}

// Stitches every per-scene Veo3 clip into one final MP4 with the
// voiceover muxed on top. Background job; poll /scenes/approval-state
// for stitched_status transitions (running → ready / failed) and
// stitched_video_url once ready.
export async function stitchAnimatedScenes(jobId) {
  const { data } = await api.post(
    `/auto-video/${jobId}/stitch-animated-scenes`,
  );
  return data;
}

export async function editScript(jobId, fields) {
  const { data } = await api.patch(`/auto-video/${jobId}/script`, fields);
  return data;
}

export async function regenerateVoiceover(jobId) {
  const { data } = await api.post(`/auto-video/${jobId}/voiceover/regenerate`);
  return data;
}
export async function previewVoiceover({ voice_id, speed, text, with_alignment }) {
  // Returns { data_url: "data:audio/mpeg;base64,...", speed, voice_id }
  // The frontend can drop data_url straight into <audio src>.
  // When with_alignment=true, also returns char_start_times_sec +
  // marker_positions_orig + marker_timestamps_sec for the cursor sync.
  const { data } = await api.post("/auto-video/voiceover/preview", {
    voice_id,
    speed,
    text,
    with_alignment: !!with_alignment,
  });
  return data;
}
export async function cancelVideoRender(jobId) {
  const { data } = await api.post(`/auto-video/${jobId}/render/cancel`);
  return data;
}
export async function renderAutoVideo(jobId, opts = {}) {
  const body = {};
  if (opts.videoProvider) body.video_provider = opts.videoProvider;
  if (opts.imageProvider) body.image_provider = opts.imageProvider;
  if (opts.force) body.force = true;
  const { data } = await api.post(`/auto-video/${jobId}/render`, body);
  return data;
}
export async function updateSceneModes(jobId, modes) {
  const { data } = await api.patch(`/auto-video/${jobId}/scene-modes`, { modes });
  return data;
}
export async function listImageProviders() {
  const { data } = await api.get("/image-providers");
  return data;
}
export async function getRenderBudget() {
  const { data } = await api.get("/render-budget");
  return data;
}

export async function listVideoProviders() {
  const { data } = await api.get("/video-providers");
  return data;
}
export async function generateThumbnail(jobId, opts = {}) {
  const { referenceFile, notes } = opts;
  // If the caller passed a reference image or notes, send as multipart.
  // Otherwise hit the endpoint with an empty body — keeps the existing
  // "Regenerate" button working unchanged.
  if (referenceFile || (notes && notes.trim())) {
    const form = new FormData();
    if (referenceFile) form.append("reference", referenceFile);
    if (notes && notes.trim()) form.append("notes", notes.trim());
    const { data } = await api.post(`/auto-video/${jobId}/thumbnail`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  }
  const { data } = await api.post(`/auto-video/${jobId}/thumbnail`);
  return data;
}

// --- Saved channels (niche shortlist) ---
export async function listSavedChannels() {
  const { data } = await api.get("/saved-channels");
  return data;
}
export async function saveChannel(channel) {
  const { data } = await api.post("/saved-channels", channel);
  return data;
}
export async function unsaveChannel(channel_id) {
  const { data } = await api.delete(`/saved-channels/${channel_id}`);
  return data;
}

// --- Saved kits ---
export async function listSavedKits() {
  const { data } = await api.get("/saved-kits");
  return data;
}
export async function getSavedKit(kitKey) {
  const { data } = await api.get(`/saved-kits/${kitKey}`);
  return data;
}
export async function saveKit(payload) {
  const { data } = await api.post("/saved-kits", payload);
  return data;
}
export async function unsaveKit(kitKey) {
  const { data } = await api.delete(`/saved-kits/${kitKey}`);
  return data;
}


// --- Replace / revert per-scene image ---
export async function replaceSceneImage({ jobId, sceneIdx, file, onProgress }) {
  // Multipart form. Backend letterboxes to 1920×1080, marks the scene
  // as user-replaced, and sets video_stale=true so the UI can prompt a
  // re-render. 50 MB upload cap (validated server-side too).
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(
    `/auto-video/${jobId}/scenes/${sceneIdx}/replace-image`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60_000,
      onUploadProgress: onProgress,
    },
  );
  return data;
}

export async function revertSceneImage({ jobId, sceneIdx }) {
  const { data } = await api.post(
    `/auto-video/${jobId}/scenes/${sceneIdx}/revert-image`,
  );
  return data;
}

// Regenerate channel-kit graphics for an existing auto-video job.
// Pass `target` (e.g. "banner_1.png") to re-roll JUST that image while
// preserving the other two — preserves prompts so the new image stays
// stylistically consistent with the rest of the kit.
export async function regenerateAutoVideoChannelKit(jobId, { target } = {}) {
  const { data } = await api.post(
    `/auto-video/${jobId}/channel-kit/regenerate`,
    null,
    {
      timeout: 180_000,
      params: target ? { target } : undefined,
    },
  );
  return data;
}


// Restore an archived banner version back into the active slot. The
// currently-active banner gets re-archived so the user can flip back
// and forth between concepts as many times as they like.
export async function restoreAutoVideoChannelKitImage(jobId, { target, archive_filename }) {
  const { data } = await api.post(
    `/auto-video/${jobId}/channel-kit/restore`,
    { target, archive_filename },
    { timeout: 30_000 },
  );
  return data;
}

// Backfill YouTube availability badges on kit name options. Used by
// AutoVideo when the saved kit predates the availability-check feature.
export async function checkAutoVideoChannelKitNames(jobId) {
  const { data } = await api.post(
    `/auto-video/${jobId}/channel-kit/check-names`,
    null,
    { timeout: 60_000 },
  );
  return data;
}

// Generate a fresh batch of channel-name suggestions for this job's
// kit, excluding any names already shown. Availability badges are
// attached automatically.
export async function refreshAutoVideoChannelKitNames(jobId) {
  const { data } = await api.post(
    `/auto-video/${jobId}/channel-kit/refresh-names`,
    null,
    { timeout: 90_000 },
  );
  return data;
}



// Read the AI image-gen prompt currently in effect for one scene.
// Returns both `default_prompt` (auto-built from scene text) and
// `override` (user's saved override, if any). `effective_prompt`
// is whichever is actually used at render time.
export async function getScenePrompt(jobId, sceneIdx) {
  const { data } = await api.get(
    `/auto-video/${jobId}/scenes/${sceneIdx}/prompt`,
    { timeout: 15_000 },
  );
  return data;
}

// Save a custom AI prompt override for one scene. Marks video as stale.
export async function saveScenePrompt(jobId, sceneIdx, prompt) {
  const { data } = await api.put(
    `/auto-video/${jobId}/scenes/${sceneIdx}/prompt`,
    { prompt },
    { timeout: 15_000 },
  );
  return data;
}

// Drop the override so the next render uses the auto-built default.
export async function resetScenePrompt(jobId, sceneIdx) {
  const { data } = await api.delete(
    `/auto-video/${jobId}/scenes/${sceneIdx}/prompt`,
    { timeout: 15_000 },
  );
  return data;
}


// ---------- Channel sharing / collaborators ----------
export async function getChannelShare(channelId) {
  const { data } = await api.get(
    `/active-channels/${channelId}/share`,
    { params: { _t: Date.now() } },
  );
  return data;
}

export async function inviteCollaborator(channelId, email) {
  const { data } = await api.post(
    `/active-channels/${channelId}/share/invite`,
    { email },
  );
  return data;
}

export async function removeCollaborator(channelId, email) {
  const { data } = await api.delete(
    `/active-channels/${channelId}/share/collaborators/${encodeURIComponent(email)}`,
  );
  return data;
}


// Replaces the auto-generated thumbnail on an Idea Bank materialize
// packet with a user-supplied image. Returns the new public URL (with
// cache-bust suffix) + provider="user-upload".
export async function uploadMaterializeThumbnail(
  { channelId, ideaId, file, onProgress },
) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(
    `/active-channels/${channelId}/idea-bank/${ideaId}/upload-thumbnail`,
    form,
    {
      timeout: 60_000,
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    },
  );
  return data;
}


// ---------- Scene-to-Video Agent (standalone batch image→video) ----------
export async function createSceneToVideoJob({
  files, prompts = [], defaultPrompt = "", title = "", videoProvider = "",
  onProgress,
}) {
  const form = new FormData();
  for (const file of files) form.append("images", file);
  for (const p of prompts) form.append("prompts", p || "");
  form.append("default_prompt", defaultPrompt || "");
  form.append("title", title || "");
  if (videoProvider) form.append("video_provider", videoProvider);
  const { data } = await api.post(
    "/scene-to-video/jobs",
    form,
    {
      // Veo3 jobs run in the background, but uploading 20 high-res
      // stills can take a moment on slow connections. Give it 5 min.
      timeout: 5 * 60 * 1000,
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    },
  );
  return data;
}

export async function getActiveSceneToVideoJob() {
  const { data } = await api.get("/scene-to-video/jobs/active", {
    params: { _t: Date.now() },
  });
  return data;
}

export async function getSceneToVideoJob(jobId) {
  const { data } = await api.get(`/scene-to-video/jobs/${jobId}`, {
    params: { _t: Date.now() },
  });
  return data;
}

export async function cancelSceneToVideoJob(jobId) {
  const { data } = await api.post(`/scene-to-video/jobs/${jobId}/cancel`);
  return data;
}

export async function retryFailedSceneToVideoScenes(jobId, { videoProvider = null } = {}) {
  const { data } = await api.post(
    `/scene-to-video/jobs/${jobId}/retry-failed`,
    { video_provider: videoProvider || null },
  );
  return data;
}

export async function generateSceneToVideoDialogue(jobId, { onlyFailed = false } = {}) {
  const { data } = await api.post(
    `/scene-to-video/jobs/${jobId}/generate-dialogue`,
    { only_failed: !!onlyFailed },
    { timeout: 120 * 1000 },
  );
  return data;
}

export async function rewriteDialogueForPrompt(prompt) {
  const { data } = await api.post(
    `/video-providers/rewrite-dialogue`,
    { prompt },
    { timeout: 120 * 1000 },
  );
  return data;
}

// ---------- Storyboards ----------
export async function listStoryboards() {
  const { data } = await api.get(`/storyboards`);
  return data;
}

// ---------- Review & Approve ----------
export async function listReviewQueue() {
  const { data } = await api.get(`/storyboards/review-queue`);
  return data;
}

export async function setStoryboardApproval(storyboardId, status) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/approval`,
    { status },
  );
  return data;
}

export async function finishStoryboardAutomation(storyboardId) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/finish-automation`,
  );
  return data;
}

export async function createStoryboard({ videoName, channelId = null, channelHandle = null } = {}) {
  // channelHandle triggers the backend to auto-apply this channel's
  // saved settings (style_preset / post_format / voice / default cast)
  // onto the new storyboard doc. Fields the caller supplies explicitly
  // still win — settings only fill in the gaps.
  const { data } = await api.post(`/storyboards`, {
    video_name: videoName,
    channel_id: channelId,
    channel_handle: channelHandle,
  });
  return data;
}

export async function getStoryboard(storyboardId) {
  const { data } = await api.get(`/storyboards/${storyboardId}`);
  return data;
}

export async function updateStoryboardMeta(
  storyboardId,
  { videoName, seoDescription, stillsMotion, postFormat, voiceId, sourceStory } = {},
) {
  const body = {};
  if (videoName !== undefined) body.video_name = videoName;
  if (seoDescription !== undefined) body.seo_description = seoDescription;
  if (stillsMotion !== undefined) body.stills_motion = stillsMotion;
  if (postFormat !== undefined) body.post_format = postFormat;
  if (voiceId !== undefined) body.voice_id = voiceId;
  if (sourceStory !== undefined) body.source_story = sourceStory;
  const { data } = await api.patch(`/storyboards/${storyboardId}`, body);
  return data;
}

export async function generateStoryboardDescription(storyboardId) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/generate-description`,
    {},
    { timeout: 90 * 1000 },
  );
  return data;
}

export async function regenerateStoryboardThumbnail(
  storyboardId,
  { characterIds = null, notes = "", stylePreset = null, fromScratch = false } = {},
) {
  const body = {};
  if (Array.isArray(characterIds)) body.character_ids = characterIds;
  if (notes && notes.trim()) body.notes = notes.trim();
  if (stylePreset !== null && stylePreset !== undefined) {
    body.style_preset = stylePreset;
  }
  if (fromScratch) body.from_scratch = true;
  const { data } = await api.post(
    `/storyboards/${storyboardId}/thumbnail/generate`,
    body,
    { timeout: 120 * 1000 },
  );
  return data;
}

export async function uploadStoryboardThumbnail(storyboardId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(
    `/storyboards/${storyboardId}/thumbnail/upload`,
    fd,
    { timeout: 60 * 1000, headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function deleteStoryboard(storyboardId) {
  const { data } = await api.delete(`/storyboards/${storyboardId}`);
  return data;
}

export async function addStoryboardScene(
  storyboardId,
  { file, description, dialog = "", audio = "", camera = "", lighting = "" },
) {
  const fd = new FormData();
  fd.append("image", file, file.name);
  fd.append("description", description || "");
  fd.append("dialog", dialog || "");
  fd.append("audio", audio || "");
  fd.append("camera", camera || "");
  fd.append("lighting", lighting || "");
  const { data } = await api.post(
    `/storyboards/${storyboardId}/scenes`, fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120 * 1000 },
  );
  return data;
}

export async function updateStoryboardScene(
  storyboardId, sceneId,
  { file = null, description = null, dialog = null, audio = null, camera = null, lighting = null, imagePromptOverride = null, videoPromptOverride = null } = {},
) {
  const fd = new FormData();
  if (file) fd.append("image", file, file.name);
  // Always include the field if it was explicitly passed (even as
  // empty string) so the user can clear it. `null` means "don't touch".
  if (description !== null && description !== undefined) fd.append("description", description);
  if (dialog !== null && dialog !== undefined) fd.append("dialog", dialog);
  if (audio !== null && audio !== undefined) fd.append("audio", audio);
  if (camera !== null && camera !== undefined) fd.append("camera", camera);
  if (lighting !== null && lighting !== undefined) fd.append("lighting", lighting);
  if (imagePromptOverride !== null && imagePromptOverride !== undefined) {
    fd.append("image_prompt_override", imagePromptOverride);
  }
  if (videoPromptOverride !== null && videoPromptOverride !== undefined) {
    fd.append("video_prompt_override", videoPromptOverride);
  }
  const { data } = await api.patch(
    `/storyboards/${storyboardId}/scenes/${sceneId}`, fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120 * 1000 },
  );
  return data;
}

export async function deleteStoryboardScene(storyboardId, sceneId) {
  const { data } = await api.delete(
    `/storyboards/${storyboardId}/scenes/${sceneId}`,
  );
  return data;
}

export async function startStoryboardScenePreview(storyboardId, sceneId, { correctionNote = "", provider = "" } = {}) {
  const body = { correction_note: correctionNote || "" };
  if (provider) body.provider = provider;
  const { data } = await api.post(
    `/storyboards/${storyboardId}/scenes/${sceneId}/preview`,
    body,
  );
  return data;
}

export async function assembleStoryboard(storyboardId) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/assemble`,
    {},
    { timeout: 30_000 },
  );
  return data;
}

export async function resetStoryboardAssemble(storyboardId) {
  const { data } = await api.post(`/storyboards/${storyboardId}/assemble/reset`);
  return data;
}

export async function approveSceneVersion(storyboardId, sceneId, versionId) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/scenes/${sceneId}/approve`,
    { version_id: versionId },
  );
  return data;
}

export async function cancelScenePreview(storyboardId, sceneId) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/scenes/${sceneId}/preview/cancel`,
  );
  return data;
}

export async function deleteSceneVersion(storyboardId, sceneId, versionId) {
  const { data } = await api.delete(
    `/storyboards/${storyboardId}/scenes/${sceneId}/versions/${versionId}`,
  );
  return data;
}


// ---------- Character consistency check ----------
// Fire-and-poll: POST starts a background Vision-LLM scan that
// compares every scene's characters against the storyboard's cast
// references. GET polls for progress. Full scan takes ~30-90s
// depending on scene count.

export async function startCharacterCheck(storyboardId) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/character-check`,
    {},
    { timeout: 20_000 },
  );
  return data;
}

export async function getCharacterCheck(storyboardId) {
  const { data } = await api.get(
    `/storyboards/${storyboardId}/character-check`,
    { timeout: 15_000 },
  );
  return data;
}

export async function getCostSummary(window = "all") {
  const { data } = await api.get(`/cost-summary`, {
    params: { window },
    timeout: 30_000,
  });
  return data;
}


// ---------- Channel-driven Story Idea Generator ----------
export async function analyzeChannelForIdeas(handle, { forceRefresh = false } = {}) {
  const { data } = await api.post(`/storyboards/channel/analyze`, {
    handle,
    force_refresh: forceRefresh,
  }, { timeout: 90_000 });
  return data;
}

export async function generateChannelIdeas(handle, { excludeTitles = [], count = 10, inspirationHandle = "", theme = "", topicsToAvoid = [] } = {}) {
  const { data } = await api.post(`/storyboards/channel/ideas`, {
    handle,
    exclude_titles: excludeTitles,
    count,
    inspiration_handle: inspirationHandle || null,
    theme: theme || null,
    topics_to_avoid: topicsToAvoid,
  }, { timeout: 120_000 });
  return data;
}


export async function automateStoryboardFromIdea(handle, idea) {
  const { data } = await api.post(`/storyboards/channel/automate-from-idea`, {
    handle, idea,
  }, { timeout: 90_000 });
  return data;
}


export async function getAutomationStatus(jobId) {
  const { data } = await api.get(`/storyboards/automate/${jobId}`);
  return data;
}


export async function listAutomationQueue() {
  const { data } = await api.get(`/storyboards/automate-queue`);
  return data;
}

export async function cancelAutomationJob(jobId) {
  const { data } = await api.post(
    `/storyboards/automate/${jobId}/cancel`,
  );
  return data;
}

export async function resumeOrphanedAutomateRuns() {
  // Manually re-launches the background worker for any of the
  // current user's `automation_jobs` whose `status="running"`.
  // Idempotent — the worker's resume-check skips scenes that
  // already rendered cleanly. Use when the activity log goes
  // silent after a backend restart / deploy.
  const { data } = await api.post(
    `/storyboards/automate/resume-orphans`,
  );
  return data;
}

// --- My Story Ideas ----------------------------------------------
// Bookmark ideas from the Channel Analysis generator so they survive
// page refresh + can be Automated one-tap from the saved list.

export async function listStoryIdeas(status) {
  const { data } = await api.get(
    `/story-ideas`,
    { params: status ? { status } : {} },
  );
  return data;
}

export async function saveStoryIdea(handle, idea) {
  const { data } = await api.post(`/story-ideas`, { handle, idea });
  return data;
}

export async function deleteStoryIdea(ideaId) {
  const { data } = await api.delete(`/story-ideas/${ideaId}`);
  return data;
}

export async function automateStoryIdea(ideaId) {
  const { data } = await api.post(
    `/story-ideas/${ideaId}/automate`,
    {},
    { timeout: 90_000 },
  );
  return data;
}

export async function publishStoryIdea(ideaId, youtubeUrl) {
  const { data } = await api.post(
    `/story-ideas/${ideaId}/publish`,
    { youtube_url: (youtubeUrl || "").trim() || null },
  );
  return data;
}

export async function unpublishStoryIdea(ideaId) {
  const { data } = await api.delete(`/story-ideas/${ideaId}/publish`);
  return data;
}

export async function createStoryboardFromIdea(handle, idea) {
  const { data } = await api.post(`/storyboards/channel/create-from-idea`, {
    handle,
    idea,
  }, { timeout: 180_000 });
  return data;
}

export async function saveChannelProfile(handle, profile) {
  const { data } = await api.put(`/storyboards/channel/profile`, {
    handle, profile,
  }, { timeout: 30_000 });
  return data;
}

export async function regenerateChannelProfile(handle) {
  const { data } = await api.post(`/storyboards/channel/profile/regenerate`, {
    handle,
  }, { timeout: 120_000 });
  return data;
}

export async function uploadChannelReferenceImage(handle, file) {
  const fd = new FormData();
  fd.append("handle", handle);
  fd.append("file", file);
  const { data } = await api.post(
    `/storyboards/channel/profile/reference-image`,
    fd,
    {
      timeout: 60_000,
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return data;
}

export async function deleteChannelReferenceImage(handle, url) {
  const { data } = await api.delete(
    `/storyboards/channel/profile/reference-image`,
    { data: { handle, url }, timeout: 30_000 },
  );
  return data;
}

export async function uploadCharacterReferenceImage(handle, character, file) {
  const fd = new FormData();
  fd.append("handle", handle);
  fd.append("character", character);
  fd.append("file", file);
  const { data } = await api.post(
    `/storyboards/channel/profile/character-reference`,
    fd,
    { timeout: 60_000, headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function deleteCharacterReferenceImage(handle, character, url) {
  const { data } = await api.delete(
    `/storyboards/channel/profile/character-reference`,
    { data: { handle, character, url }, timeout: 30_000 },
  );
  return data;
}

export async function deleteCharacter(handle, character) {
  const { data } = await api.delete(
    `/storyboards/channel/profile/character`,
    { data: { handle, character }, timeout: 30_000 },
  );
  return data;
}

export async function addCharacter(handle, { name, species = "", outfit = "", accessories = [] } = {}) {
  const { data } = await api.post(
    `/storyboards/channel/profile/character`,
    { handle, name, species, outfit, accessories },
    { timeout: 30_000 },
  );
  return data;
}

export async function renameCharacter(handle, oldName, newName) {
  const { data } = await api.patch(
    `/storyboards/channel/profile/character`,
    { handle, old_name: oldName, new_name: newName },
    { timeout: 30_000 },
  );
  return data;
}

export async function consolidateCharacterRefs(handle) {
  const { data } = await api.post(
    `/storyboards/channel/profile/consolidate-refs`,
    { handle },
    { timeout: 30_000 },
  );
  return data;
}

export async function generateSceneImage(
  storyboardId, sceneId,
  { provider, prompt, tweak = false, fromScratch = false } = {},
) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/scenes/${sceneId}/generate-image`,
    {
      provider,
      prompt: prompt || null,
      tweak: !!tweak,
      from_scratch: !!fromScratch,
    },
    { timeout: 180_000 },
  );
  return data;
}

export async function regenerateAllSceneImages(
  storyboardId,
  { provider, fromScratch = false } = {},
) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/scenes/regenerate-all`,
    {
      provider: provider || undefined,
      from_scratch: !!fromScratch,
    },
    { timeout: 30_000 },
  );
  return data;
}

export async function spellcheckSceneImage(storyboardId, sceneId) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/scenes/${sceneId}/spellcheck`,
    {},
    { timeout: 90_000 },
  );
  return data;
}

export async function spellcheckThumbnail(storyboardId) {
  const { data } = await api.post(
    `/storyboards/${storyboardId}/thumbnail/spellcheck`,
    {},
    { timeout: 90_000 },
  );
  return data;
}

export async function uploadSceneReferenceImage(storyboardId, sceneId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(
    `/storyboards/${storyboardId}/scenes/${sceneId}/reference-image`,
    fd,
    { timeout: 60_000, headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function deleteSceneReferenceImage(storyboardId, sceneId, url) {
  const { data } = await api.delete(
    `/storyboards/${storyboardId}/scenes/${sceneId}/reference-image`,
    { data: { url }, timeout: 30_000 },
  );
  return data;
}

export async function getScenePrompts(storyboardId, sceneId) {
  const { data } = await api.get(
    `/storyboards/${storyboardId}/scenes/${sceneId}/prompts`,
    { timeout: 30_000 },
  );
  return data;
}


// ---------- Top YouTube Videos (last 30 days) ----------
export async function getTopVideos30d() {
  const { data } = await api.get("/top-videos-30d", {
    params: { _t: Date.now() },
    timeout: 60_000,
  });
  return data;
}

export async function refreshTopVideos30d(force = true) {
  const { data } = await api.post(
    "/top-videos-30d/scan",
    null,
    { params: { force }, timeout: 60_000 },
  );
  return data;
}

export async function getTopNiches30d() {
  const { data } = await api.get("/top-niches-30d", {
    params: { _t: Date.now() },
    timeout: 90_000,
  });
  return data;
}

export async function refreshTopNiches30d(force = true) {
  const { data } = await api.post(
    "/top-niches-30d/scan",
    null,
    { params: { force }, timeout: 120_000 },
  );
  return data;
}

export async function getStyleGallery() {
  const { data } = await api.get("/style-gallery", { timeout: 30_000 });
  return data;
}


// ---------- Per-user style-preset reference images ----------
// Users upload 1-4 refs per preset via the Style Gallery card;
// backend auto-attaches them to every scene / preview / thumbnail
// render that uses that preset (via gpt-image-1's /edits endpoint).

export async function listStylePresetRefs(presetKey) {
  const { data } = await api.get(
    `/style-presets/${encodeURIComponent(presetKey)}/references`,
    { timeout: 15_000 },
  );
  return data; // { preset_key, refs: [{ref_id, url, filename, ...}], max }
}

export async function uploadStylePresetRef(presetKey, file, { onProgress } = {}) {
  const fd = new FormData();
  fd.append("file", file, file.name);
  // Deliberately do NOT set `Content-Type` here — axios + the browser
  // will emit `multipart/form-data; boundary=…` automatically. Setting
  // it manually as just "multipart/form-data" (no boundary) has been
  // observed to trigger a CORS preflight that drops the session cookie
  // on some Chrome + Cloudflare edges, surfacing as a 401
  // "Not authenticated" — even though the browser IS logged in.
  const { data } = await api.post(
    `/style-presets/${encodeURIComponent(presetKey)}/references`,
    fd,
    {
      timeout: 60_000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    },
  );
  return data; // { preset_key, ref }
}

export async function deleteStylePresetRef(presetKey, refId) {
  const { data } = await api.delete(
    `/style-presets/${encodeURIComponent(presetKey)}/references/${encodeURIComponent(refId)}`,
    { timeout: 15_000 },
  );
  return data;
}


// ---------- Provider Shootout — animate 1 still on every model ----------
export async function startProviderShootout({ file, prompt, providers = [] }) {
  const form = new FormData();
  form.append("image", file);
  if (prompt) form.append("prompt", prompt);
  for (const pid of providers) form.append("providers", pid);
  const { data } = await api.post("/video-providers/shootout", form, {
    timeout: 5 * 60 * 1000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function getProviderShootout(testId) {
  const { data } = await api.get(`/video-providers/shootout/${testId}`, {
    params: { _t: Date.now() },
  });
  return data;
}

// ---- AI render log (admin) ----------------------------------------
export async function listRenderLog(params = {}) {
  const { data } = await api.get("/admin/render-log", {
    params: { ...params, _t: Date.now() },
  });
  return data;
}



